/**
 * The Reach Wire generator (spec §12.1). Two sources feed `state.wire`:
 * - **Day items** from a faction-day rollup (`generateDayWire`, run in the day's last slice): shortages (a
 *   traded ware below `shortageFill` all day), surpluses (above `surplusFill` all day), price shocks (a unit
 *   price `priceShock` or more off the day-start baseline), stations built, ships built (per yard) and
 *   depleted fields.
 * - **Immediate items** from notable events as they happen (`immediateWire` / `scanImmediateWire`): lost
 *   convoys, raids and completed stations; M9 (CH-14b): a disabled hull left in a system the player sees live
 *   (`disabledHullWire`, its own per-day cap, never the M4 budget).
 *
 * Every candidate passes the same gate, in rank order (severity, then magnitude, then a fixed tie order):
 * 1. **Merge:** an item of the same kind for the same station (or system, when it names no station) and ware
 *    within `mergeDays` merges into the earlier one (the new one is dropped).
 * 2. **Rate:** at most `maxPerDay` generated items per faction day Reach-wide, plus up to `localExtra` more
 *    that are local (the player's system or within `localJumps` of it, or a ware in the player's hold).
 *    Items count toward the day they are posted in; injected `notice` items never count.
 * Accepted items get a cause (shortage and surplus sides, `causes.ts`), a seeded headline variant
 * (`templates.ts`; a shortage also ends with its competing-haulers count, TR-7) and the next
 * `counters.wireSeq` id, and are appended to `state.wire` (trimmed to strategic.json `wireKeep`).
 *
 * `postWire` is the injection hook for M5 and M7: its items bypass merge and rate and always post.
 * Runs once per day and per notable event, never inside a per-agent loop, so it may allocate.
 */
import { z } from 'zod'
import wireM7Json from '../../data/strategic/wire-m7.json'
import wireM9Json from '../../data/strategic/wire-m9.json'
import { systemId as formatSystemId } from '../../galaxy/placement'
import { eventSlot, firstReadable } from '../events'
import type { WireTemplateKey } from '../schemas'
import { EV, FACTION_NONE, STORY_WIRE_OUTLETS, TRADE_BIT, WRECK_CLAIM, type DayRollup, type FactionId, type StoryWireOutlet, type StrategicState, type WireItem, type WireKind } from '../types'
import { WARE_COUNT, WARE_IDS } from '../wares'
import { priceDelta } from '../view/priceCauses'
import { isDisabledHullHeadline } from './disabledHullLine'
import {
  attributeCause,
  createCause,
  factionIdOf,
  factionName,
  jumpsBetween,
  renderCause,
  stationName,
  systemName,
  wareName,
  type Cause,
  type EffectSide,
  type WireContext,
} from './causes'
import { haulersLine, pickVariant, render, renderHeadline, type WireFields } from './templates'

/** What the generator reads and writes: the cause context plus the Wire list, its serial and past rollups. */
export type WireState = WireContext & Pick<StrategicState, 'counters' | 'wire' | 'rollups' | 'wrecks' | 'commissions'>

/** The player's side of relevance: their system (−1 = none) and the ware indices in their hold. */
export interface PlayerWireContext {
  sys: number
  wares: readonly number[]
}

export const NO_PLAYER: PlayerWireContext = { sys: -1, wares: [] }

/** Every field of a Wire item except the ones `postWire` assigns (id, time, day). */
export type WirePost = Omit<WireItem, 'id' | 't' | 'day'>

/** Tie order of kinds at equal severity and magnitude. */
const KIND_ORDER: Record<WireKind, number> = {
  convoyLost: 0,
  raid: 1,
  shortage: 2,
  priceShock: 3,
  surplus: 4,
  stationBuilt: 5,
  shipsBuilt: 6,
  fieldDepleted: 7,
  notice: 8,
}

interface Candidate {
  kind: Exclude<WireKind, 'notice'>
  key: WireTemplateKey
  /** 0..1 within the severity: how far past the threshold, how many ships. */
  magnitude: number
  sys: number
  /** −1 = none. */
  station: number
  /** −1 = none. */
  ware: number
  faction: number
  /** A second faction (the raid's target), FACTION_NONE = none. */
  other: number
  /** Attribute a cause on this side; null = no cause. */
  side: EffectSide | null
}

function candidate(
  kind: Candidate['kind'],
  key: WireTemplateKey,
  magnitude: number,
  sys: number,
  station: number,
  ware: number,
  faction: number,
  other: number,
  side: EffectSide | null,
): Candidate {
  return { kind, key, magnitude, sys, station, ware, faction, other, side }
}

function severityOf(state: WireState, kind: Candidate['kind']): 1 | 2 | 3 {
  return state.data.wire.severity[kind]
}

function compareCandidates(state: WireState, a: Candidate, b: Candidate): number {
  return (
    severityOf(state, b.kind) - severityOf(state, a.kind) ||
    b.magnitude - a.magnitude ||
    KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
    a.station - b.station ||
    a.ware - b.ware ||
    a.sys - b.sys
  )
}

function systemIdOf(state: WireState, sys: number): string {
  return state.galaxy.systems[sys]?.id ?? formatSystemId(sys)
}

function isLocal(state: WireState, player: PlayerWireContext, sys: number, ware: number): boolean {
  if (player.sys >= 0 && jumpsBetween(state.routing, player.sys, sys) <= state.data.wire.localJumps) return true
  return ware >= 0 && player.wares.includes(ware)
}

/** Generated (non-`notice`) items posted today. */
function postedToday(state: WireState): number {
  let n = 0
  for (let i = state.wire.length - 1; i >= 0; i--) {
    const item = state.wire[i]
    if (item.day < state.day) break
    if (item.day === state.day && item.kind !== 'notice') n++
  }
  return n
}

/** True when an item of the same kind, place and ware was posted within `mergeDays`. */
function merges(state: WireState, kind: WireKind, stationId: string | undefined, systemId: string, ware: string | undefined): boolean {
  const from = state.time - state.data.wire.mergeDays * state.data.strategic.clock.dayS
  for (let i = state.wire.length - 1; i >= 0; i--) {
    const item = state.wire[i]
    if (item.t < from) break
    if (item.kind !== kind || item.ware !== ware) continue
    if (stationId !== undefined ? item.stationId === stationId : item.stationId === undefined && item.systemId === systemId) return true
  }
  return false
}

function append(state: Pick<StrategicState, 'wire' | 'data'>, item: WireItem, out: WireItem[] | undefined): void {
  state.wire.push(item)
  const excess = state.wire.length - state.data.strategic.wireKeep
  if (excess > 0) state.wire.splice(0, excess)
  if (out !== undefined) out.push(item)
}

const causeScratch: Cause = createCause()

/**
 * TR-7: the live agents already carrying ware `ware` to station `station` (a delivery committed with
 * `incoming` > 0 at that `dst`), the "competing haulers en route" of a shortage item. Read-only: the Wire
 * never feeds back into the sim.
 */
export function haulersEnRoute(state: Pick<StrategicState, 'agents'>, station: number, ware: number): number {
  const ag = state.agents
  let n = 0
  for (let a = 0; a < ag.high; a++) {
    if (ag.alive[a] === 1 && ag.dst[a] === station && ag.ware[a] === ware && ag.incoming[a] > 0) n++
  }
  return n
}

/** Merge, rate-limit, attribute, render and append one candidate. Returns the item, or null when merged or over the rate. */
function tryPost(state: WireState, c: Candidate, player: PlayerWireContext, out: WireItem[] | undefined, force = false): WireItem | null {
  const stationId = c.station >= 0 ? state.stations.id[c.station] : undefined
  const systemId = systemIdOf(state, c.sys)
  const wareId = c.ware >= 0 && c.ware < WARE_COUNT ? WARE_IDS[c.ware] : undefined
  if (!force && merges(state, c.kind, stationId, systemId, wareId)) return null
  const today = postedToday(state)
  const w = state.data.wire
  if (!force && !(today < w.maxPerDay || (today < w.maxPerDay + w.localExtra && isLocal(state, player, c.sys, c.ware)))) return null

  const id = state.counters.wireSeq
  let causeText = ''
  let cause: WireItem['cause']
  if (c.side !== null && c.station >= 0 && c.ware >= 0) {
    attributeCause(state, { side: c.side, station: c.station, ware: c.ware }, causeScratch)
    causeText = renderCause(state, causeScratch, id)
    cause = { kind: causeScratch.kind, text: causeText.trim() }
  }
  let headline = renderHeadline(state.data, state.seed, c.key, id, {
    station: c.station >= 0 ? stationName(state, c.station, c.sys) : '',
    system: systemName(state, c.sys),
    ware: c.ware >= 0 ? wareName(state, c.ware) : '',
    faction: factionName(state, c.faction),
    cause: causeText,
  })
  // TR-7: a shortage says how many haulers are already heading there (text only; no new item).
  if (c.kind === 'shortage' && c.station >= 0 && c.ware >= 0) headline = `${headline} ${haulersLine(haulersEnRoute(state, c.station, c.ware))}`
  const factions: FactionId[] = []
  const f1 = factionIdOf(c.faction)
  const f2 = factionIdOf(c.other)
  if (f1 !== null) factions.push(f1)
  if (f2 !== null && f2 !== f1) factions.push(f2)

  const item: WireItem = { id, t: state.time, day: state.day, kind: c.kind, severity: severityOf(state, c.kind), systemId, headline, factions }
  if (stationId !== undefined) item.stationId = stationId
  if (wareId !== undefined) item.ware = wareId
  if (cause !== undefined) item.cause = cause
  state.counters.wireSeq = id + 1
  append(state, item, out)
  return item
}

function postAll(state: WireState, candidates: Candidate[], player: PlayerWireContext, out: WireItem[] | undefined): number {
  candidates.sort((a, b) => compareCandidates(state, a, b))
  const w = state.data.wire
  let posted = 0
  for (const c of candidates) {
    if (postedToday(state) >= w.maxPerDay + w.localExtra) break
    if (tryPost(state, c, player, out) !== null) posted++
  }
  return posted
}

/**
 * The day items of `rollup` (spec §12.1), posted through merge and rate. The price-shock baseline is the
 * previous day's rollup baseline (the baseline at this day's start) when it is still kept, else this
 * rollup's. Returns the number of items posted; new items are also pushed to `out`.
 */
export function generateDayWire(state: WireState, rollup: DayRollup, player: PlayerWireContext = NO_PLAYER, out?: WireItem[]): number {
  const { data, stations: st } = state
  const w = data.wire
  const candidates: Candidate[] = []
  let prevBaseline = rollup.baseline
  for (const r of state.rollups) if (r.day === rollup.day - 1 && r.baseline.length === rollup.baseline.length) prevBaseline = r.baseline

  const n = Math.min(rollup.stationCount, st.count)
  for (let s = 0; s < n; s++) {
    for (let ware = 0; ware < WARE_COUNT; ware++) {
      const k = s * WARE_COUNT + ware
      if ((st.trade[k] & (TRADE_BIT.buys | TRADE_BIT.sells)) === 0 || !(st.cap[k] > 0)) continue
      const fmax = rollup.fillMax[k]
      const fmin = rollup.fillMin[k]
      if (fmax < w.shortageFill) {
        candidates.push(candidate('shortage', 'shortage', 1 - fmax / w.shortageFill, st.sys[s], s, ware, st.owner[s], FACTION_NONE, 'short'))
      } else if (fmin > w.surplusFill) {
        const span = 1 - w.surplusFill
        candidates.push(candidate('surplus', 'surplus', span > 0 ? (fmin - w.surplusFill) / span : 1, st.sys[s], s, ware, st.owner[s], FACTION_NONE, 'glut'))
      } else {
        const delta = priceDelta(data, rollup.fillEnd[k], prevBaseline[k])
        if (Math.abs(delta) >= w.priceShock) {
          const up = delta > 0
          const key = up ? 'priceUp' : 'priceDown'
          candidates.push(candidate('priceShock', key, Math.min(1, Math.abs(delta)), st.sys[s], s, ware, st.owner[s], FACTION_NONE, up ? 'short' : 'glut'))
        }
      }
    }
  }

  for (const s of rollup.newStations) {
    if (s >= 0 && s < st.count) candidates.push(candidate('stationBuilt', 'stationBuilt', 1, st.sys[s], s, -1, st.owner[s], FACTION_NONE, null))
  }

  // Ships built and fields depleted during the rollup's day, from the notable ring.
  const ring = state.notable
  const dayS = data.strategic.clock.dayS
  const dayStart = rollup.day * dayS
  const dayEnd = dayStart + dayS
  const builds = new Map<number, number[]>()
  for (let i = firstReadable(ring, 0); i < ring.head; i++) {
    const k = eventSlot(ring, i)
    const t = ring.t[k]
    if (t < dayStart || t >= dayEnd) continue
    const type = ring.type[k]
    if (type === EV.shipBuilt) {
      const yard = ring.station[k]
      if (yard < 0 || yard >= st.count) continue
      let perFaction = builds.get(yard)
      if (perFaction === undefined) {
        perFaction = new Array<number>(data.factions.factions.length).fill(0)
        builds.set(yard, perFaction)
      }
      const f = ring.faction[k]
      if (f < perFaction.length) perFaction[f]++
    } else if (type === EV.fieldDepleted) {
      candidates.push(candidate('fieldDepleted', 'fieldDepleted', 1, ring.sys[k], -1, ring.ware[k], FACTION_NONE, FACTION_NONE, null))
    }
  }
  for (const yard of [...builds.keys()].sort((a, b) => a - b)) {
    const perFaction = builds.get(yard) ?? []
    let total = 0
    let top = 0
    for (let f = 0; f < perFaction.length; f++) {
      total += perFaction[f]
      if (perFaction[f] > perFaction[top]) top = f
    }
    candidates.push(candidate('shipsBuilt', 'shipsBuilt', total / (total + 1), st.sys[yard], yard, -1, top, FACTION_NONE, null))
  }

  return postAll(state, candidates, player, out)
}

/**
 * The immediate item of notable event `seq` (spec §12.1): a lost convoy, a raid, a completed station or (M9) a
 * disabled hull (`wreckCreated`, `disabledHullWire`).
 * Returns the posted item, or null (another type, unreadable, merged or over the rate).
 */
export function immediateWire(state: WireState, seq: number, player: PlayerWireContext = NO_PLAYER, out?: WireItem[]): WireItem | null {
  const ring = state.notable
  if (seq < firstReadable(ring, 0) || seq >= ring.head) return null
  const k = eventSlot(ring, seq)
  const st = state.stations
  const station = ring.station[k] >= 0 && ring.station[k] < st.count ? ring.station[k] : -1
  switch (ring.type[k]) {
    case EV.convoyLost:
      return tryPost(state, candidate('convoyLost', 'convoyLost', 1, ring.sys[k], station, ring.ware[k], ring.faction[k], FACTION_NONE, null), player, out)
    case EV.raid:
      return tryPost(state, candidate('raid', 'raid', 1, ring.sys[k], -1, -1, ring.faction[k], ring.other[k], null), player, out)
    case EV.stationBuilt:
      if (station < 0) return null
      return tryPost(state, candidate('stationBuilt', 'stationBuilt', 1, ring.sys[k], station, -1, ring.faction[k], FACTION_NONE, null), player, out)
    case EV.wreckCreated:
      return disabledHullWire(state, k, player, out)
    default:
      return null
  }
}

/** Posts the immediate items of every notable event from `cursor` on. Returns the new cursor (`notable.head`). */
export function scanImmediateWire(state: WireState, cursor: number, player: PlayerWireContext = NO_PLAYER, out?: WireItem[]): number {
  const ring = state.notable
  for (let i = firstReadable(ring, cursor); i < ring.head; i++) immediateWire(state, i, player, out)
  return ring.head
}

/**
 * Playtest R3-H3 (dev op `postShortage`): posts the shortage item of ware `ware` at station `station` now, as the
 * day rollup would (cause, headline variant and TR-7's competing-haulers count), bypassing merge and rate.
 */
export function devPostShortage(state: WireState, station: number, ware: number): WireItem {
  const st = state.stations
  const item = tryPost(state, candidate('shortage', 'shortage', 1, st.sys[station], station, ware, st.owner[station], FACTION_NONE, 'short'), NO_PLAYER, undefined, true)
  if (item === null) throw new Error('wire: a forced post was refused')
  return item
}

/**
 * The injection hook (M5 faction news, M7 story beats): posts `post` now, bypassing merge and rate, with the
 * next Wire id. Use kind `notice` for items that should not count toward the day's generated budget.
 */
export function postWire(state: Pick<StrategicState, 'counters' | 'wire' | 'time' | 'day' | 'data'>, post: WirePost): WireItem {
  const item: WireItem = { ...post, factions: [...post.factions], id: state.counters.wireSeq, t: state.time, day: state.day }
  state.counters.wireSeq++
  append(state, item, undefined)
  return item
}

// ---------------------------------------------------------------------------------------------
// M7 outlets (M7 spec §12.3; plan S17): every M7 item carries the outlet that ran it (`WireItem.source`), with
// slanted headlines per outlet from `src/data/strategic/wire-m7.json`. Story items are rare (a command, a stage
// crossing), so this may allocate.
// ---------------------------------------------------------------------------------------------

/** The M7 Wire item keys (wire-m7.json `items`). */
export const STORY_WIRE_KEYS = [
  'grievanceSet',
  'stageFalling',
  'stageRising',
  'storyTierDrop',
  'repealProposed',
  'stationRestored',
  'stationDark',
  'stationReverted',
  'registryChallenge',
] as const
export type StoryWireKey = (typeof STORY_WIRE_KEYS)[number]

const outletSchema = z.enum(STORY_WIRE_OUTLETS)
const variantsSchema = z.array(z.string().min(1)).min(1).max(4)

export const wireM7Schema = z.strictObject({
  comment: z.string(),
  items: z.record(
    z.enum(STORY_WIRE_KEYS),
    z.strictObject({
      severity: z.number().int().min(1).max(3),
      outlets: z.partialRecord(outletSchema, variantsSchema).refine((o) => Object.keys(o).length > 0, 'at least one outlet'),
    }),
  ),
  stageCauses: z.strictObject({
    falling: z.partialRecord(outletSchema, variantsSchema),
    rising: z.partialRecord(outletSchema, variantsSchema),
  }),
})
export type WireM7 = z.output<typeof wireM7Schema>

/** Parses a `wire-m7.json` (tests pass their own); throws with the path on a bad file. */
export function loadWireM7(raw: unknown): WireM7 {
  const r = wireM7Schema.safeParse(raw)
  if (r.success) return r.data
  const issue = r.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`strategic data: wire-m7.json: ${where}: ${issue?.message ?? 'invalid'}`)
}

/** The shipped `wire-m7.json`, parsed once at load. */
export const WIRE_M7: WireM7 = loadWireM7(wireM7Json)

/** The outlets that run `key`, in STORY_WIRE_OUTLETS order. */
export function outletsOf(key: StoryWireKey): StoryWireOutlet[] {
  const o = WIRE_M7.items[key].outlets
  return STORY_WIRE_OUTLETS.filter((x) => o[x] !== undefined)
}

export interface StoryWirePost {
  /** The system id the item is about (the home system when it is Reach-wide). */
  systemId: string
  factions: FactionId[]
  fields: WireFields
  /** Run by this outlet when it carries `key`; otherwise one of the key's outlets, seeded by the Wire id. */
  outlet?: StoryWireOutlet
}

/** The outlet an M7 item runs in: `wanted` when it carries the key, else a seeded pick of the key's outlets. */
export function pickOutlet(seed: number, key: StoryWireKey, id: number, wanted?: StoryWireOutlet): StoryWireOutlet {
  const list = outletsOf(key)
  if (wanted !== undefined && list.includes(wanted)) return wanted
  return pickVariant(list, seed, `wire-m7-${key}`, id)
}

/** Posts an M7 item (kind `notice`, bypassing merge and rate) with its outlet's slanted headline and `source`. */
export function postStoryWire(state: Pick<StrategicState, 'counters' | 'wire' | 'time' | 'day' | 'data' | 'seed'>, key: StoryWireKey, post: StoryWirePost): WireItem {
  const id = state.counters.wireSeq
  const source = pickOutlet(state.seed, key, id, post.outlet)
  const spec = WIRE_M7.items[key]
  const variants = spec.outlets[source] ?? []
  const text = render(pickVariant(variants, state.seed, 'wire', id), post.fields)
  const headline = text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1) : text
  const severity = spec.severity === 3 ? 3 : spec.severity === 2 ? 2 : 1
  return postWire(state, { kind: 'notice', severity, systemId: post.systemId, factions: post.factions, headline, source })
}

/** The slanted `{cause}` of a gate stage crossing for `outlet` (rising or falling), seeded by the Wire id. */
export function stageCause(seed: number, outlet: StoryWireOutlet, rising: boolean, id: number): string {
  const list = (rising ? WIRE_M7.stageCauses.rising : WIRE_M7.stageCauses.falling)[outlet]
  return list === undefined ? '' : pickVariant(list, seed, 'wire-m7-cause', id)
}

// ---------------------------------------------------------------------------------------------
// M9 disabled hulls (plan 2026-10-05-m9-claimable-hulls CH-14b, H-4): when an off-screen loss leaves a disabled hull
// (CH-1b, `WRECK_CLAIM.disabled`) in a system the player sees live, the Wire names the hull and the system so the
// player can reach it within its `claim.lifetimeDays`. The source is the `wreckCreated` event every off-screen loss
// already emits (a notable type: it reaches the notable ring the Wire scans; aux = the record's serial), so no event,
// RNG draw or sim write is added. One line per hull (the cursor reads each event once), at most
// `wire-m9.json disabledHull.maxPerDay` per faction day; a notice, so it never uses the M4 day budget. Rare (a share of
// small/medium losses in covered systems), so this may allocate.
// ---------------------------------------------------------------------------------------------

export const wireM9Schema = z.strictObject({
  comment: z.string(),
  disabledHull: z.strictObject({
    severity: z.number().int().min(1).max(3),
    maxPerDay: z.number().int().min(0).max(24),
    variants: variantsSchema,
    hullNames: z.record(z.string().min(1), z.string().min(1)),
  }),
})
export type WireM9 = z.output<typeof wireM9Schema>

/** Parses a `wire-m9.json` (tests pass their own); throws with the path on a bad file. */
export function loadWireM9(raw: unknown): WireM9 {
  const r = wireM9Schema.safeParse(raw)
  if (r.success) return r.data
  const issue = r.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`strategic data: wire-m9.json: ${where}: ${issue?.message ?? 'invalid'}`)
}

/** The shipped `wire-m9.json`, parsed once at load. */
export const WIRE_M9: WireM9 = loadWireM9(wireM9Json)

/** True when `headline` is a disabled-hull line (the leaf `disabledHullLine.ts`, shared with the Wire panel's filter). */
export { isDisabledHullHeadline }

/** Disabled-hull lines posted today (the per-day cap; read from the Wire itself, so a save and load keep it). */
export function disabledHullLinesToday(state: Pick<StrategicState, 'wire' | 'day'>): number {
  let n = 0
  for (let i = state.wire.length - 1; i >= 0; i--) {
    const item = state.wire[i]
    if (item.day < state.day) break
    if (item.day === state.day && item.kind === 'notice' && isDisabledHullHeadline(item.headline)) n++
  }
  return n
}

/**
 * True when the player sees system `sys` live (H-4, D-494's coverage): their current system (`player.sys`) or a system
 * with a built player-corp station (a live commission whose `station` is set; a site still building has no market).
 */
export function seesLive(state: Pick<StrategicState, 'commissions'>, player: PlayerWireContext, sys: number): boolean {
  if (sys < 0) return false
  if (sys === player.sys) return true
  const cm = state.commissions
  for (let c = 0; c < cm.high; c++) if (cm.alive[c] === 1 && cm.station[c] >= 0 && cm.sys[c] === sys) return true
  return false
}

/** The readable name of hull index `hull` (wire-m9.json `hullNames`, else the id with spaces). */
export function hullDisplayName(state: Pick<StrategicState, 'data'>, hull: number): string {
  const id = state.data.hulls.hulls[hull]?.id ?? ''
  return WIRE_M9.disabledHull.hullNames[id] ?? id.replace(/_/g, ' ')
}

/** The live record with serial `seq`, or −1. */
function wreckBySeq(state: Pick<StrategicState, 'wrecks'>, seq: number): number {
  const ws = state.wrecks
  for (let w = 0; w < ws.high; w++) if (ws.alive[w] === 1 && ws.seq[w] === seq) return w
  return -1
}

/**
 * The disabled-hull line of the `wreckCreated` event at notable ring slot `k`, or null: the record is gone or not
 * disabled (an ordinary wreck, or already an open claim), the system is not seen live, or the day's cap is reached.
 */
function disabledHullWire(state: WireState, k: number, player: PlayerWireContext, out: WireItem[] | undefined): WireItem | null {
  const cfg = WIRE_M9.disabledHull
  if (cfg.maxPerDay <= 0) return null
  const ring = state.notable
  const sys = ring.sys[k]
  if (!seesLive(state, player, sys)) return null
  const w = wreckBySeq(state, ring.aux[k])
  const ws = state.wrecks
  if (w < 0 || ws.claim[w] !== WRECK_CLAIM.disabled || ws.sys[w] !== sys) return null
  if (disabledHullLinesToday(state) >= cfg.maxPerDay) return null

  const id = state.counters.wireSeq
  const text = render(pickVariant(cfg.variants, state.seed, 'wire-m9-hull', id), {
    hull: hullDisplayName(state, ws.hull[w]),
    system: systemName(state, sys),
    faction: factionName(state, ring.faction[k]),
  })
  const headline = text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1) : text
  const factions: FactionId[] = []
  const f = factionIdOf(ring.faction[k])
  if (f !== null) factions.push(f)
  const severity = cfg.severity === 3 ? 3 : cfg.severity === 2 ? 2 : 1
  const item: WireItem = { id, t: state.time, day: state.day, kind: 'notice', severity, systemId: systemIdOf(state, sys), headline, factions }
  state.counters.wireSeq = id + 1
  append(state, item, out)
  return item
}
